import React, { useState, useEffect } from 'react';
import { Search, X, Loader2, Image as ImageIcon, Check } from 'lucide-react';
import { loadLibraryItems } from '../services/libraryService';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';

const MediaLibrarySelector = ({ onSelect, onClose, multiSelect = true, maxSelection = 5 }) => {
    const { currentUser } = useAuth();
    const { selectedBrand } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (currentUser && selectedBrand) {
            loadItems();
        }
    }, [currentUser, selectedBrand]);

    const loadItems = async () => {
        setLoading(true);
        try {
            const libraryItems = await loadLibraryItems(currentUser.uid, selectedBrand.id, {
                orderByField: 'createdAt'
            });
            // Filter for images only
            const images = libraryItems.filter(item =>
                !item.type || item.type === 'image' || item.type === 'ad_creative' || item.imageUrl
            );
            setItems(images);
        } catch (err) {
            console.error('Failed to load library items:', err);
            setError('Failed to load library items. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleSelect = (item) => {
        if (multiSelect) {
            if (selectedItems.find(i => i.id === item.id)) {
                setSelectedItems(prev => prev.filter(i => i.id !== item.id));
            } else {
                if (selectedItems.length >= maxSelection) return;
                setSelectedItems(prev => [...prev, item]);
            }
        } else {
            setSelectedItems([item]);
        }
    };

    const handleConfirm = () => {
        onSelect(selectedItems);
        onClose();
    };

    const filteredItems = items.filter(item =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: 'var(--space-4)',
        }}>
            <div className="card" style={{
                maxWidth: '900px',
                width: '100%',
                height: '80vh',
                display: 'flex',
                flexDirection: 'column',
                padding: 0,
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{
                    padding: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>Select from Library</h3>
                        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                            {selectedItems.length} selected {multiSelect && `(max ${maxSelection})`}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 'var(--space-2)'
                        }}
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Toolbar */}
                <div style={{
                    padding: 'var(--space-3) var(--space-4)',
                    borderBottom: '1px solid var(--color-border)',
                    background: 'var(--color-bg-secondary)'
                }}>
                    <div className="input-with-icon" style={{ maxWidth: '100%' }}>
                        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
                        <input
                            type="text"
                            className="input-field"
                            placeholder="Search images..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ paddingLeft: '36px', width: '100%' }}
                        />
                    </div>
                </div>

                {/* Grid */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: 'var(--space-4)',
                    background: 'var(--color-bg-primary)'
                }}>
                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', gap: 'var(--space-3)' }}>
                            <Loader2 className="animate-spin" size={32} color="var(--color-primary)" />
                            <p>Loading library items...</p>
                        </div>
                    ) : error ? (
                        <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--error-600)' }}>
                            {error}
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--color-text-tertiary)' }}>
                            <ImageIcon size={48} style={{ marginBottom: 'var(--space-4)', opacity: 0.5 }} />
                            <p>No images found in library</p>
                        </div>
                    ) : (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                            gap: 'var(--space-4)'
                        }}>
                            {filteredItems.map(item => {
                                const isSelected = selectedItems.find(i => i.id === item.id);
                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => handleToggleSelect(item)}
                                        style={{
                                            position: 'relative',
                                            aspectRatio: '1',
                                            borderRadius: 'var(--radius-md)',
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            border: isSelected ? '3px solid var(--color-primary)' : '1px solid var(--color-border)',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <img
                                            src={item.imageUrl}
                                            alt={item.title || 'Library Item'}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            loading="lazy"
                                        />
                                        {isSelected && (
                                            <div style={{
                                                position: 'absolute',
                                                top: 'var(--space-2)',
                                                right: 'var(--space-2)',
                                                background: 'var(--color-primary)',
                                                borderRadius: '50%',
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: 'var(--shadow-sm)'
                                            }}>
                                                <Check size={14} color="white" />
                                            </div>
                                        )}
                                        <div style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                            padding: 'var(--space-3) var(--space-2) var(--space-2)',
                                            color: 'white',
                                            fontSize: '11px',
                                            opacity: 0.9
                                        }}>
                                            <div style={{ truncate: true, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.title || item.name || 'Untitled'}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 'var(--space-3)',
                    background: 'var(--color-bg-secondary)'
                }}>
                    <button className="btn btn-ghost" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleConfirm}
                        disabled={selectedItems.length === 0}
                    >
                        Add Selected ({selectedItems.length})
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MediaLibrarySelector;
